import React, { forwardRef, useMemo, useRef } from "react";
import { Select as SelectPD } from "@pd/pd-ui-kit";
import type { SelectProps } from "@pd/pd-ui-kit";
import type { RefSelectProps } from "antd";
import type { Key } from "react";

import {
  dedupeOptionsByValue,
  filterSortSelectedFirst,
  flattenOptions,
  getOptionFromLabelInValue,
  getSelectedKeys,
  sortOptionsSelectedFirst,
} from "~/shared/lib/selectedFirstOptions";

type Option = NonNullable<SelectProps["options"]>[number];

/**
 * Обёртка над Select из @pd/pd-ui-kit: выбранные опции всегда
 * отображаются вверху выпадающего списка (single и multiple,
 * сырые значения и labelInValue). Относительный порядок опций
 * в остальном сохраняется.
 *
 * Пагинированные списки с серверным поиском: выбранная через поиск
 * опция выпадает из загруженных страниц, как только поиск сброшен
 * (хуки перезапрашивают список с page 0). Чтобы такая опция не
 * пропадала из закреплённого блока и из тега (иначе rc-select
 * показывает сырой id), обёртка кэширует опции выбранных значений
 * и подмешивает отсутствующие в текущих options. Кэш живёт, пока
 * значение выбрано, и вычищается при снятии выбора.
 */
export const Select = forwardRef<RefSelectProps, SelectProps>((props, ref) => {
  const { value, options, filterSort, onChange, onSearch, ...rest } = props;

  const selectedKeys = useMemo(() => getSelectedKeys(value), [value]);
  const selectedCacheRef = useRef(new Map<Key, Option>());

  const handleChange: SelectProps["onChange"] = (nextValue, option) => {
    onChange?.(nextValue, option);
    onSearch?.("");
  };

  const sortedOptions = useMemo(() => {
    const deduped = dedupeOptionsByValue(options ?? []);
    const cache = selectedCacheRef.current;

    cache.forEach((_, key) => {
      if (!selectedKeys.has(key)) {
        cache.delete(key);
      }
    });

    const loadedKeys = new Set<Key>();
    flattenOptions(deduped).forEach((option) => {
      if (option.value == null) return;
      loadedKeys.add(option.value);
      if (selectedKeys.has(option.value)) {
        cache.set(option.value, option);
      }
    });

    const missing: Option[] = [];
    selectedKeys.forEach((key) => {
      if (loadedKeys.has(key)) return;
      const cached =
        cache.get(key) ?? getOptionFromLabelInValue<Option>(value, key);
      if (cached) {
        cache.set(key, cached);
        missing.push(cached);
      }
    });

    return sortOptionsSelectedFirst([...missing, ...deduped], selectedKeys);
  }, [options, selectedKeys, value]);

  const filterSortFn = useMemo(
    () => filterSort ?? filterSortSelectedFirst(selectedKeys),
    [filterSort, selectedKeys],
  );

  return (
    <SelectPD
      {...rest}
      ref={ref}
      value={value}
      options={sortedOptions}
      filterSort={filterSortFn}
      onChange={handleChange}
      onSearch={onSearch}
    />
  );
});

Select.displayName = "Select";
